import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AuthContext } from "../../../../contexts/AuthContext";
import { setSubscriptionStatus } from "../../../../utils/pdf/subscriptionStatus";
import { generateFormCSDraftReviewPdf } from "./Pdf/generator";
import { StyledIconButton } from "../../../utils/Icon";

export const openFormCSPdfInNewTab = (doc, title) => {
  doc.setProperties({
    title,
  });

  const pdfBlob = doc.output("blob");

  const pdfFile = new File([pdfBlob], `${title}.pdf`, {
    type: "application/pdf",
  });

  const pdfUrl = URL.createObjectURL(pdfFile);
  const faviconUrl = `${window.location.origin}/favicon.ico`;

  const html = `
    <html>
      <head>
        <title>${title}</title>
        <link rel="icon" href="${faviconUrl}" />
        <style>
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
          }

          embed {
            width: 100%;
            height: 100%;
            border: none;
          }
        </style>
      </head>
      <body>
        <embed src="${pdfUrl}" type="application/pdf" />
      </body>
    </html>
  `;

  const htmlBlob = new Blob([html], { type: "text/html" });
  const htmlUrl = URL.createObjectURL(htmlBlob);

  window.open(htmlUrl, "_blank");
};

/**
 * Form C-S themed control — opens an internal draft PDF for review only.
 */
const FormCSDraftReviewPdf = ({
  company,
  filingInfo,
  dataFormCS,
  declaration,
  declarationAck,
  selectedTc,
  referenceSummary,
  disabled,
}) => {
  const { global_authState } = useContext(AuthContext);
  const [loadingAction, setLoadingAction] = useState(null);

  useEffect(() => {
    setSubscriptionStatus(global_authState?.subscription?.status || null);
  }, [global_authState?.subscription?.status]);

  const ya = filingInfo?.ya ?? "";
  const uen = filingInfo?.uen || "—";

  const loading = Boolean(loadingAction);

  const getDraftPdf = useCallback(
    () =>
      generateFormCSDraftReviewPdf(
        true,
        company,
        { filingInfo, dataFormCS, declaration, declarationAck },
        { selectedTc, referenceSummary }
      ),
    [
      company,
      filingInfo,
      dataFormCS,
      declaration,
      declarationAck,
      selectedTc,
      referenceSummary,
    ]
  );

  const getDraftPdfFilename = useCallback(() => {
    const cleanName = "Form C-S Draft Review";

    return `${company.uen}_${cleanName}.pdf`;
  }, []);

  const handleViewDraft = useCallback(async () => {
    if (disabled || loading) return;
    setLoadingAction("view");
    try {
      const doc = getDraftPdf();
      openFormCSPdfInNewTab(doc, `Form C-S Draft Review`);
    } catch (err) {
      console.warn("Form C-S draft PDF failed:", err);
    } finally {
      setLoadingAction(null);
    }
  }, [disabled, getDraftPdf, loading]);

  const handleDownloadDraft = useCallback(async () => {
    if (disabled || loading) return;
    setLoadingAction("download");
    try {
      const doc = getDraftPdf();
      doc.save(getDraftPdfFilename());
    } catch (err) {
      console.warn("Form C-S draft PDF download failed:", err);
    } finally {
      setLoadingAction(null);
    }
  }, [disabled, getDraftPdf, getDraftPdfFilename, loading]);

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "3px solid",
        borderLeftColor: "primary.main",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent='space-between'
      >
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='flex-start'
          sx={{ flex: 1 }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <AccountBalanceOutlinedIcon fontSize='small' />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              flexWrap='wrap'
            >
              <Typography variant='subtitle2' fontWeight={700}>
                Submission draft review
              </Typography>
              <Chip
                label='DRAFT'
                size='small'
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                  color: "warning.dark",
                }}
              />
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Preview all Form C-S details in a single PDF for verification
              purposes.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              <b>
                This PDF is for checking and verifying your Form C-S details
                purpose only and will not be included in the submission to IRAS.
              </b>
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: "block", mt: 0.75 }}
            >
              UEN {uen} · YA {ya || "—"}
            </Typography>
          </Box>
        </Stack>

        <StyledIconButton
          variant='outlined'
          icon={DownloadIcon}
          iconSize={24}
          onClick={handleDownloadDraft}
          disabled={loadingAction === "download"}
          fullWidth
          sx={{
            borderColor: "primary.main",
            "&:hover": {
              borderColor: "primary.dark",
              backgroundColor: "primary.lighter",
            },
          }}
          tooltip='Download Form C-S Draft Review'
        />

        <Button
          variant='contained'
          color='primary'
          disabled={disabled || loading}
          onClick={handleViewDraft}
          startIcon={
            loading ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <VisibilityOutlinedIcon />
            )
          }
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            minWidth: { sm: 168 },
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: 1 },
          }}
        >
          {loading ? "Opening draft…" : "View draft PDF"}
        </Button>
      </Stack>
    </Box>
  );
};

export default FormCSDraftReviewPdf;
